import { DirectoryDto } from './../../../../data-transfer/entities/directory-dto';
import { Component, Optional, Inject, computed } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DialogActions } from 'src/app/model/dictionary';
import { debounceTime, distinctUntilChanged, map, tap } from 'rxjs/operators';

const SEARCH_DEBOUNCE_MS = 200;

export class SelectLocationDialogData {
  rootFolder!: DirectoryDto;
  unreachableFolders: DirectoryDto[] = [];
  searchable?: boolean;
}

@Component({
  selector: 'app-select-location-dialog',
  templateUrl: './select-location-dialog.component.html',
  styleUrls: ['./select-location-dialog.component.scss'],
})
export class SelectLocationDialogComponent {

  rootFolder: DirectoryDto;
  unreachableFolders: DirectoryDto[];
  selectedFolder?: DirectoryDto;
  readonly searchable: boolean;

  readonly searchControl = new FormControl('', { nonNullable: true });

  private readonly appliedTerm = toSignal(
    this.searchControl.valueChanges.pipe(
      debounceTime(SEARCH_DEBOUNCE_MS),
      map(term => term.trim().toLowerCase()),
      distinctUntilChanged(),
      tap(() => (this.selectedFolder = undefined))
    ),
    { initialValue: '' }
  );
  private readonly foldersById = new Map<number, DirectoryDto>();

  readonly filteredRoot = computed(() => {
    const term = this.appliedTerm();
    return term ? this.filterFolder(this.rootFolder, term) : this.rootFolder;
  });

  readonly treeRoots = computed(() => {
    const root = this.filteredRoot();
    return root ? [root] : [];
  });

  readonly expandedFolderIds = computed(() => {
    const root = this.filteredRoot();
    if (!root) {
      return [];
    }
    const term = this.appliedTerm();
    return term ? [...new Set([root.id, ...this.collectExpandableIds(root, term)])] : [root.id];
  });

  constructor(
    private dialogRef: MatDialogRef<SelectLocationDialogComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) protected data: SelectLocationDialogData,
  ) {
    this.rootFolder = data.rootFolder;
    this.unreachableFolders = data.unreachableFolders;
    this.searchable = data.searchable ?? false;
    this.indexFolders(this.rootFolder);
  }

  setSelectedFolder(folder: DirectoryDto): void {
    this.selectedFolder = folder;
  }

  acceptChanges() {
    if (!this.selectedFolder) {
      return;
    }
    this.dialogRef.close({ data: this.foldersById.get(this.selectedFolder.id) ?? this.selectedFolder });
  }

  discardChanges() {
    this.dialogRef.close({ event: DialogActions.REJECT });
  }

  isUnreacheable() {
    if (!this.unreachableFolders || this.unreachableFolders.length === 0 || !this.selectedFolder) { return false; }
    return this.unreachableFolders?.map(x => x?.id).includes(this.selectedFolder?.id);
  }

  private indexFolders(folder: DirectoryDto): void {
    this.foldersById.set(folder.id, folder);
    folder.childDirectories?.forEach(child => this.indexFolders(child));
  }

  private matches(folder: DirectoryDto, term: string): boolean {
    return folder.name.toLowerCase().includes(term);
  }

  private filterFolder(folder: DirectoryDto, term: string): DirectoryDto | null {
    if (this.matches(folder, term)) {
      return folder;
    }
    const children = (folder.childDirectories ?? [])
      .map(child => this.filterFolder(child, term))
      .filter((child): child is DirectoryDto => child !== null);
    return children.length > 0 ? { ...folder, childDirectories: children } : null;
  }

  private collectExpandableIds(folder: DirectoryDto, term: string): number[] {
    if (this.matches(folder, term)) {
      return [];
    }
    return [folder.id, ...(folder.childDirectories ?? []).flatMap(child => this.collectExpandableIds(child, term))];
  }
}
